<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { api, extractError } from '@/api/client'
import { useKeysStore } from '@/stores/keys'
import { useServerHealthStore } from '@/stores/serverHealth'
import { locationParts } from '@/utils/countries'
import type { VpnKey } from '@/types'

export interface AvailableServerItem {
  id: number
  name: string
  location: string
  display_location?: string
  country_code?: string | null
  protocol_labels?: string[]
  protocols_text?: string
}

export interface AvailableServersStats {
  servers: number
  protocols: number
  keys: number
  configs: number
}

const props = defineProps<{
  manualKeys: VpnKey[]
}>()

const keysStore = useKeysStore()
const serverHealth = useServerHealthStore()

const loading = ref(false)
const error = ref('')
const servers = ref<AvailableServerItem[]>([])
const stats = ref<AvailableServersStats | null>(null)

const userServerIds = computed(() => {
  const ids = new Set<number>()
  for (const key of props.manualKeys) {
    if (key.server_id) ids.add(key.server_id)
    if (key.display_server_id) ids.add(key.display_server_id)
  }
  return ids
})

const gridServerIds = computed(() => servers.value.map((s) => s.id))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get<{
      success: boolean
      servers?: AvailableServerItem[]
      stats?: AvailableServersStats
      message?: string
    }>('/api/user/available-servers')
    if (!data.success) {
      error.value = data.message || 'Не удалось загрузить серверы'
      servers.value = []
      stats.value = null
      return
    }
    servers.value = data.servers || []
    stats.value = data.stats || null
    if (gridServerIds.value.length > 0) {
      await serverHealth.refreshStatus(gridServerIds.value)
    }
  } catch (e) {
    error.value = extractError(e)
    servers.value = []
    stats.value = null
  } finally {
    loading.value = false
  }
}

function flagUrl(srv: AvailableServerItem): string | null {
  return locationParts(srv.country_code, srv.location, 'ru').flagUrl
}

function cardOpacity(serverId: number): number {
  return userServerIds.value.has(serverId) ? 1 : 0.6
}

watch(
  () => keysStore.lastFetch,
  () => {
    if (servers.value.length > 0) void load()
  },
)

onMounted(() => void load())
</script>

<template>
  <section v-if="!loading && servers.length > 0" class="mb-8">
    <h2 class="mb-2 text-base font-bold">
      Доступные сервера
    </h2>

    <div v-if="stats" class="alert alert-info mb-3 text-sm">
      По подписке доступно {{ stats.servers }} серверов и {{ stats.protocols }} протоколов.
      Общее количество ключей: {{ stats.keys }}.
      Различные конфигурации ключей: {{ stats.configs }}.
    </div>

    <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>

    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      <div
        v-for="srv in servers"
        :key="srv.id"
        class="panel h-full border border-white-light !p-2 text-center"
        :style="{ opacity: cardOpacity(srv.id) }"
      >
        <div class="mb-1 flex justify-center">
          <img
            v-if="flagUrl(srv)"
            :src="flagUrl(srv)!"
            width="28"
            height="21"
            class="rounded-sm object-cover"
            :alt="srv.display_location || srv.location"
            loading="lazy"
          >
        </div>
        <h3 class="mb-1 text-xs font-bold leading-tight">
          {{ srv.name }}
        </h3>
        <p class="mb-1 text-[0.7rem] text-muted">
          {{ srv.display_location || srv.location }}
        </p>
        <span class="badge text-[0.7rem]" :class="serverHealth.pingBadgeClass(srv.id)">
          {{ serverHealth.pingLabel(srv.id) }}
        </span>
        <p v-if="srv.protocols_text" class="mt-2 text-[0.7rem] leading-snug">
          <span class="text-muted">Доступные протоколы:</span><br>
          <span>{{ srv.protocols_text }}</span>
        </p>
      </div>
    </div>
  </section>

  <div v-else-if="loading" class="mb-8 text-sm text-muted">
    Загрузка доступных серверов…
  </div>
</template>
